import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds/components/icon";
import type { HTMLAttributes, ReactNode, Ref } from "react";
import { forwardRef, useCallback, useState } from "react";
import {
  GettingStartedCardSurface,
  StyledExpandButton,
  StyledHeader,
  StyledHeaderContainer,
  StyledHeaderTitle,
} from "./getting-started-card.style";

export interface GettingStartedCardFrameProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  canCollapse?: boolean;
  children: ReactNode;
  collapseLabel?: string;
  headerAccessory?: ReactNode;
  initiallyCollapsed?: boolean;
  isExpanded?: boolean;
  isDismissible?: boolean;
  onDismiss?: () => void;
  onToggleExpanded?: (isExpanded: boolean) => void;
  title: ReactNode;
  toggleButtonRef?: Ref<HTMLButtonElement>;
}

export const GettingStartedCardFrame = forwardRef<
  HTMLDivElement,
  GettingStartedCardFrameProps
>(function GettingStartedCardFrame(
  {
    canCollapse = true,
    children,
    collapseLabel = "Collapse",
    headerAccessory,
    initiallyCollapsed,
    isExpanded: controlledIsExpanded,
    isDismissible = false,
    onDismiss,
    onToggleExpanded,
    title,
    toggleButtonRef,
    ...surfaceProps
  },
  ref
) {
  const [internalIsExpanded, setInternalIsExpanded] = useState(!initiallyCollapsed);
  const isExpanded = controlledIsExpanded ?? internalIsExpanded;

  const handleToggleExpanded = useCallback(() => {
    const nextIsExpanded = !isExpanded;

    if (controlledIsExpanded === undefined) {
      setInternalIsExpanded(nextIsExpanded);
    }

    onToggleExpanded?.(nextIsExpanded);
  }, [controlledIsExpanded, isExpanded, onToggleExpanded]);

  const handleControlClick = useCallback(() => {
    if (isDismissible) {
      onDismiss?.();
      return;
    }

    handleToggleExpanded();
  }, [handleToggleExpanded, isDismissible, onDismiss]);

  const shouldShowControl = isDismissible || canCollapse;

  return (
    <GettingStartedCardSurface ref={ref} {...surfaceProps}>
      <StyledHeaderContainer>
        <StyledHeader>
          <StyledHeaderTitle>{title}</StyledHeaderTitle>
          {shouldShowControl && (
            <StyledExpandButton
              aria-label={isDismissible ? "Dismiss" : isExpanded ? collapseLabel : "Expand"}
              onClick={handleControlClick}
              ref={toggleButtonRef}
              type="button"
            >
              <MDSIcon
                color={MDSIconColor.Placeholder}
                kind={
                  isDismissible
                    ? MDSIconKind.Exit
                    : isExpanded
                      ? MDSIconKind.AngleDown
                      : MDSIconKind.AngleRight
                }
                size={12}
              />
            </StyledExpandButton>
          )}
        </StyledHeader>
        {headerAccessory}
      </StyledHeaderContainer>

      {isExpanded && children}
    </GettingStartedCardSurface>
  );
});
